'use client';

import { c as _c } from "react/compiler-runtime";
import type { ExperimentClient, Variants } from '@amplitude/experiment-js-client';
import { Experiment, Source } from '@amplitude/experiment-js-client';
import { usePathname } from 'next/navigation';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { configPublic } from '@src/config.public';
import { useHasMounted } from '@src/hooks/useHasMounted';
import { getAnalyticsTarget } from '@src/utils/analytics-target.util';
import { getExperiment } from '@src/utils/experiment.util';
import { log } from '@src/utils/log.util';
const {
  clientDeploymentKey,
  clientDeploymentKeyDigital
} = configPublic.amplitude;
export interface ExperimentClientWithConfig {
  experimentClient: ExperimentClient | null;
  allVariants: Variants | null;
}
export const ExperimentContext = createContext<ExperimentClientWithConfig>({
  experimentClient: null,
  allVariants: null
});
export const ExperimentProvider = t0 => {
  const $ = _c(12);
  const {
    children,
    experiments
  } = t0;
  const hasMounted = useHasMounted();
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname) {
    t1 = getAnalyticsTarget(pathname);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const target = t1;
  const deploymentKey = target === "digital" && clientDeploymentKeyDigital ? clientDeploymentKeyDigital : clientDeploymentKey;
  let t2;
  bb0: {
    if (!hasMounted) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[2] !== deploymentKey || $[3] !== experiments || $[4] !== target) {
      t3 = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
        initialVariants: experiments,
        automaticExposureTracking: false,
        source: Source.InitialVariants,
        instanceName: target === "digital" ? "digital" : undefined
      });
      $[2] = deploymentKey;
      $[3] = experiments;
      $[4] = target;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const experimentClient = t2;
  let t3;
  if ($[6] !== experimentClient || $[7] !== experiments) {
    t3 = {
      experimentClient,
      allVariants: experiments
    };
    $[6] = experimentClient;
    $[7] = experiments;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== t3) {
    t4 = <ExperimentContext.Provider value={t3}>{children}</ExperimentContext.Provider>;
    $[9] = children;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
export function useExperimentContext() {
  const context = useContext(ExperimentContext);
  if (!context) {
    log.warn("useExperimentContext must be used within an ExperimentContext Provider");
    return null;
  }
  return context;
}
export function useExperiment(experimentKey) {
  const $ = _c(6);
  const {
    allVariants,
    experimentClient
  } = useExperimentContext() ?? {};
  let t0;
  if ($[0] !== allVariants || $[1] !== experimentKey) {
    t0 = getExperiment(allVariants, experimentKey);
    $[0] = allVariants;
    $[1] = experimentKey;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== experimentClient || $[4] !== t0) {
    t1 = {
      ...t0,
      experimentClient
    };
    $[3] = experimentClient;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}